import { Button, THEME } from "@myloc/myloc-gui";
import classNames from "classnames";
import PropTypes from "prop-types";
import { useTranslate } from "../../../language/i18n";
import { setError } from "../../../reducers/dialog/dialogAction";
import pages from "../../../utils/pages";
import { BankIDLoginAlternatives } from "../../shared/BankId/BankIdAuthentication";
import { BANKID } from "../../shared/BankId/constants";
import SpinningButton from "../../shared/Buttons/MuiButton/MuiButton";
import { loginWithEntra, loginWithOpenId } from "../../shared/openId/utils/openIdUtils";
import Login from "../Login/Login";
import useAvailableLoginMethods, { UI_LOGIN_METHODS } from "./hooks/useAvailableLoginMethods";
import useSelectedLoginMethod from "./hooks/useSelectedLoginMethod";
import styles from "./LoginWelcome.module.scss";
import Welcome from "./Welcome";

const LoginFormLogin = ({ isSelected, onSelect, ...props }) => {
  const translate = useTranslate();
  return (
    <div className={styles.loginFormContainer} onClick={onSelect}>
      {isSelected ? (
        <Login {...props} registerUrl={pages.REGISTER} forgotPasswordUrl={"/"} />
      ) : (
        <SpinningButton>{translate("AUTHENTICATE_WITH") + " " + translate("USERNAME")}</SpinningButton>
      )}
    </div>
  );
};
LoginFormLogin.propTypes = {
  isSelected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
};

const BankIDSelector = ({ isSelected, onSelect, children }) => {
  const translate = useTranslate();

  return (
    <>
      {isSelected ? (
        children
      ) : (
        <div onClick={onSelect}>
          <SpinningButton>{translate("AUTHENTICATE_WITH") + " " + BANKID}</SpinningButton>
        </div>
      )}
    </>
  );
};

BankIDSelector.propTypes = {
  children: PropTypes.node.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
};

const Logins = ({
  availableUILoginMethods,
  isLoginFormSelected,
  isBankIDSelected,
  setSelectedLoginMethod,
  ...props
}) => {
  const translate = useTranslate();
  return (
    <div className={styles.loginFormsOuterContainer}>
      <div className={styles.loginFormContainer}>
        {availableUILoginMethods.length > 1 && !(isLoginFormSelected || isBankIDSelected) && (
          <div className={classNames(styles.sectionTitle, styles.textAlignLeft)}>
            {translate("SELECT") + " " + translate("AUTHENTICATION_METHOD")}
          </div>
        )}
      </div>
      {availableUILoginMethods.map(method => {
        if (method === UI_LOGIN_METHODS.BANKID && !isLoginFormSelected) {
          return (
            <div className={styles.loginFormContainer} key={method}>
              <BankIDSelector isSelected={isBankIDSelected} onSelect={() => setSelectedLoginMethod(method)}>
                <BankIDLoginAlternatives />
              </BankIDSelector>
            </div>
          );
        } else if (method === UI_LOGIN_METHODS.LOGIN_FORM && !isBankIDSelected) {
          return (
            <LoginFormLogin
              key={method}
              isSelected={isLoginFormSelected}
              onSelect={() => setSelectedLoginMethod(method)}
              {...props}
            />
          );
        } else if (method === UI_LOGIN_METHODS.OPEN_ID && !isBankIDSelected && !isLoginFormSelected) {
          return (
            <div className={styles.loginFormContainer} key={method}>
              <SpinningButton
                buttonAction={async () => {
                  try {
                    loginWithOpenId();
                  } catch (error) {
                    if (error instanceof Error) {
                      setError({ errorMessage: error.message });
                    }
                  }
                }}
              >
                {process.env.REACT_APP_OPEN_ID_LOGIN_TEXT}
              </SpinningButton>
            </div>
          );
        } else if (method === UI_LOGIN_METHODS.ENTRA && !isBankIDSelected && !isLoginFormSelected) {
          return (
            <div className={styles.loginFormContainer} key={method}>
              <SpinningButton
                buttonAction={async () => {
                  try {
                    loginWithEntra();
                  } catch (error) {
                    if (error instanceof Error) {
                      setError({ errorMessage: error.message });
                    }
                  }
                }}
              >
                {process.env.REACT_APP_ENTRA_LOGIN_TEXT}
              </SpinningButton>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
};
Logins.propTypes = {
  availableUILoginMethods: PropTypes.arrayOf(PropTypes.string).isRequired,
  isLoginFormSelected: PropTypes.bool.isRequired,
  isBankIDSelected: PropTypes.bool.isRequired,
  setSelectedLoginMethod: PropTypes.func.isRequired,
};

const LoginMethodsContainer = ({ availableUILoginMethods, reset, selectedLoginMethod, ...props }) => {
  const translate = useTranslate();
  return (
    <>
      <Logins availableUILoginMethods={availableUILoginMethods} {...props} />
      {selectedLoginMethod && availableUILoginMethods.length > 1 && (
        <div className={styles.loginFormContainer} onClick={reset}>
          <Button theme={THEME.PLAIN_PRIMARY}>{translate("CHANGE") + " " + translate("AUTHENTICATION_METHOD")}</Button>
        </div>
      )}
    </>
  );
};
LoginMethodsContainer.propTypes = {
  availableUILoginMethods: PropTypes.arrayOf(PropTypes.string).isRequired,
  reset: PropTypes.func.isRequired,
  selectedLoginMethod: PropTypes.string,
};

const LoginWelcome = ({ ...props }) => {
  const { availableUILoginMethods } = useAvailableLoginMethods();
  const {
    selectedLoginMethod,
    setSelectedLoginMethod,
    reset,
    isLoginFormSelected,
    isBankIDSelected,
  } = useSelectedLoginMethod(availableUILoginMethods);

  return (
    <Welcome {...props}>
      <LoginMethodsContainer
        availableUILoginMethods={availableUILoginMethods}
        reset={reset}
        selectedLoginMethod={selectedLoginMethod}
        setSelectedLoginMethod={setSelectedLoginMethod}
        isLoginFormSelected={isLoginFormSelected}
        isBankIDSelected={isBankIDSelected}
        {...props}
      />
    </Welcome>
  );
};

LoginWelcome.propTypes = {
  title: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  customCssClass: PropTypes.string,
};

export default LoginWelcome;
